import type { Listenbereich, Slot, SlotFelder as Felder, SlotUeberschreibung } from "@gui-creator/gemeinsam";
import { Eraser, Move } from "lucide-react";
import { useState } from "react";
import { BlattZu } from "../huelle/Blatt.tsx";
import { FelderEditor, type FeldAenderung } from "./FelderEditor.tsx";
import { Abschnitt, menuePlatzhalter, mische, quelleVon, ZustandReiter, type Kontext } from "./gemeinsam.tsx";
import { ItemWahl } from "./ItemWahl.tsx";

export function SlotFelder({ k, index }: { k: Kontext; index: number }) {
  const slot: Slot | undefined = k.ui.daten.slots[index];
  const [zustand, setZustand] = useState<string | null>(null);
  const zustaende = quelleVon(k.katalog, k.ui.daten.quelle)?.menueZustaende ?? [];
  const setze = (felder: Record<string, unknown>) => k.aendern([{ art: "slot_setzen", index, felder } as never]);

  if (!slot) {
    return (
      <div className="panel-inhalt">
        <header className="panel-kopf">
          <h2>Slot {index}</h2>
          <span className="leise">Leer, im Spiel Rahmenglas</span>
          <BlattZu />
        </header>
        <Abschnitt titel="Belegen">
          <ItemWahl label="Item" wert={undefined} leer="Item wählen" deaktiviert={!k.darf} onWert={(s) => setze({ symbol: s })} />
        </Abschnitt>
        {k.ui.daten.listen.length > 0 && k.darf && <BlaetternZuweisen k={k} index={index} />}
      </div>
    );
  }

  const { zustaende: ueber, ...basis } = slot;
  const aktiv = zustand && zustaende.includes(zustand) ? zustand : null;
  const abweichung: SlotUeberschreibung = (aktiv && ueber?.[aktiv]) || {};

  const setzen = (a: FeldAenderung) => {
    if (!aktiv) return setze(a as Record<string, unknown>);
    const neu = { ...(ueber ?? {}), [aktiv]: mische(abweichung, a) };
    setze({ zustaende: neu });
  };

  return (
    <div className="panel-inhalt">
      <header className="panel-kopf">
        <h2>Slot {index}</h2>
        {k.darf && (
          <span className="panel-kopf-knoepfe">
            {k.verschieben && (
              <button type="button" className="knopf knopf-still" onClick={() => k.verschieben!(index)} title="Danach den Zielslot wählen">
                <Move size={14} aria-hidden="true" /> Verschieben
              </button>
            )}
            <button type="button" className="knopf knopf-still" onClick={() => k.aendern([{ art: "slots_leeren", indizes: [index] }])}>
              <Eraser size={14} aria-hidden="true" /> Leeren
            </button>
          </span>
        )}
        <BlattZu />
      </header>
      <ZustandReiter zustaende={zustaende} aktiv={aktiv} onWahl={setZustand} belegt={(z) => !!ueber?.[z]} />
      {aktiv && (
        <div className="zustand-optionen">
          <label className="schalter">
            <input type="checkbox" checked={!!abweichung.leer} disabled={!k.darf} onChange={(e) => setzen({ leer: e.target.checked ? true : null } as FeldAenderung)} />
            <span>In „{aktiv}“ leer lassen</span>
          </label>
          {ueber?.[aktiv] && k.darf && (
            <button
              type="button"
              className="knopf knopf-still"
              onClick={() => {
                const rest = { ...ueber };
                delete rest[aktiv];
                setze({ zustaende: Object.keys(rest).length ? rest : null });
              }}
            >
              Abweichung entfernen
            </button>
          )}
        </div>
      )}
      {!abweichung.leer && <FelderEditor k={k} felder={aktiv ? (abweichung as Partial<Felder>) : basis} {...(aktiv ? { basis } : {})} setzen={setzen} platzhalter={menuePlatzhalter(k)} />}
    </div>
  );
}

function BlaetternZuweisen({ k, index }: { k: Kontext; index: number }) {
  const setzeBlaettern = (l: Listenbereich, art: "zurueck" | "weiter" | "info") => {
    const neu: Listenbereich = { ...l, blaettern: { ...(l.blaettern ?? {}), [art]: { slot: index } } };
    k.aendern([{ art: "listenbereich_setzen", bereich: neu }]);
  };
  return (
    <Abschnitt titel="Als Blätter-Slot">
      {k.ui.daten.listen.map((l) => (
        <div key={l.name} className="feld">
          <span className="feld-titel">Listenbereich „{l.name}“</span>
          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={() => setzeBlaettern(l, "zurueck")}>
              Zurück
            </button>
            <button type="button" className="knopf" onClick={() => setzeBlaettern(l, "info")}>
              Seitenanzeige
            </button>
            <button type="button" className="knopf" onClick={() => setzeBlaettern(l, "weiter")}>
              Weiter
            </button>
          </div>
        </div>
      ))}
    </Abschnitt>
  );
}
